import React, { useState, useRef } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import DocumentPreview from './components/DocumentPreview';
import FolderStructure from './components/FolderStructure';
import DocumentTable from './components/DocumentTable';
import UploadModal from './components/UploadModal';
import ShareModal from './components/ShareModal';
import VersionHistory from './components/VersionHistory';

const DocumentManagement = () => {
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const mockDocuments = [
    {
      id: 1,
      name: "Mwangi vs Kamau - Contract Dispute.pdf",
      type: "pdf",
      size: "2.4 MB",
      uploadDate: "2024-01-15",
      lastModified: "2024-01-15",
      modifiedBy: "Sarah Wanjiku",
      version: "1.0",
      caseId: "CASE-2024-001",
      caseName: "Mwangi vs Kamau",
      category: "Court Filing",
      tags: ["contract", "dispute", "commercial"],
      status: "Final",
      sharedWith: ["client", "opposing-counsel"],
      url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
    },
    {
      id: 2,
      name: "Land Title Deed - Nairobi Property.pdf",
      type: "pdf",
      size: "1.8 MB",
      uploadDate: "2024-01-14",
      lastModified: "2024-01-14",
      modifiedBy: "John Kiprotich",
      version: "2.1",
      caseId: "CASE-2024-002",
      caseName: "Njoroge Property Transfer",
      category: "Legal Document",
      tags: ["property", "title-deed", "real-estate"],
      status: "Under Review",
      sharedWith: ["client"],
      url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
    },
    {
      id: 3,
      name: "Employment Contract - Safaricom Ltd.docx",
      type: "docx",
      size: "156 KB",
      uploadDate: "2024-01-13",
      lastModified: "2024-01-13",
      modifiedBy: "Mary Achieng",
      version: "3.0",
      caseId: "CASE-2024-003",
      caseName: "Employment Dispute - Safaricom",
      category: "Contract",
      tags: ["employment", "contract", "corporate"],
      status: "Draft",
      sharedWith: [],
      url: "https://file-examples.com/storage/fe68c8a7c69bd447d7c4e6c/2017/10/file_example_DOC_10kB.doc"
    },
    {
      id: 4,
      name: "Court Ruling - High Court Nairobi.pdf",
      type: "pdf",
      size: "3.2 MB",
      uploadDate: "2024-01-12",
      lastModified: "2024-01-12",
      modifiedBy: "David Mutua",
      version: "1.0",
      caseId: "CASE-2024-004",
      caseName: "Constitutional Petition No. 123",
      category: "Court Document",
      tags: ["ruling", "high-court", "constitutional"],
      status: "Final",
      sharedWith: ["client", "court"],
      url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
    },
    {
      id: 5,
      name: "Client Agreement - Wanjiku & Associates.pdf",
      type: "pdf",
      size: "890 KB",
      uploadDate: "2024-01-11",
      lastModified: "2024-01-11",
      modifiedBy: "Grace Wanjiku",
      version: "1.2",
      caseId: "CASE-2024-005",
      caseName: "Retainer Agreement",
      category: "Agreement",
      tags: ["retainer", "client-agreement", "fees"],
      status: "Executed",
      sharedWith: ["client"],
      url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"
    },
    {
      id: 6,
      name: "Evidence Photos - Accident Scene.zip",
      type: "zip",
      size: "15.6 MB",
      uploadDate: "2024-01-10",
      lastModified: "2024-01-10",
      modifiedBy: "Peter Ochieng",
      version: "1.0",
      caseId: "CASE-2024-006",
      caseName: "Motor Vehicle Accident Claim",
      category: "Evidence",
      tags: ["evidence", "photos", "accident"],
      status: "Under Review",
      sharedWith: ["investigator"],
      url: "#"
    }
  ];

  const mockFolders = [
    {
      id: 'all',
      name: 'All Documents',
      icon: 'FileText',
      count: 6,
      children: []
    },
    {
      id: 'cases',
      name: 'Cases',
      icon: 'Briefcase',
      count: 4,
      children: [
        { id: 'case-001', name: 'Mwangi vs Kamau', count: 1 },
        { id: 'case-002', name: 'Njoroge Property Transfer', count: 1 },
        { id: 'case-003', name: 'Employment Dispute - Safaricom', count: 1 },
        { id: 'case-004', name: 'Constitutional Petition No. 123', count: 1 }
      ]
    },
    {
      id: 'clients',
      name: 'Clients',
      icon: 'Users',
      count: 3,
      children: [
        { id: 'client-001', name: 'James Mwangi', count: 2 },
        { id: 'client-002', name: 'Sarah Njoroge', count: 1 },
        { id: 'client-003', name: 'Safaricom Ltd', count: 1 }
      ]
    },
    {
      id: 'templates',
      name: 'Templates',
      icon: 'FileTemplate',
      count: 0,
      children: [
        { id: 'contracts', name: 'Contracts', count: 0 },
        { id: 'pleadings', name: 'Pleadings', count: 0 },
        { id: 'letters', name: 'Letters', count: 0 }
      ]
    },
    {
      id: 'shared',
      name: 'Shared with Me',
      icon: 'Share2',
      count: 2,
      children: []
    },
    {
      id: 'recent',
      name: 'Recent',
      icon: 'Clock',
      count: 6,
      children: []
    }
  ];

  const documentTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'pdf', label: 'PDF Documents' },
    { value: 'docx', label: 'Word Documents' },
    { value: 'xlsx', label: 'Excel Files' },
    { value: 'zip', label: 'Archives' },
    { value: 'image', label: 'Images' }
  ];

  const sortOptions = [
    { value: 'date', label: 'Date Modified' },
    { value: 'name', label: 'Name' },
    { value: 'size', label: 'File Size' },
    { value: 'type', label: 'File Type' }
  ];

  const filteredDocuments = mockDocuments?.filter(doc => {
    const matchesSearch = doc?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         doc?.caseName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         doc?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    
    const matchesType = filterType === 'all' || doc?.type === filterType;
    
    const matchesFolder = selectedFolder === 'all' || 
                         (selectedFolder === 'recent') ||
                         (selectedFolder?.startsWith('case-') && doc?.caseId?.includes(selectedFolder?.split('-')?.[1])) ||
                         (selectedFolder === 'shared' && doc?.sharedWith?.length > 0);
    
    return matchesSearch && matchesType && matchesFolder;
  });

  const sortedDocuments = [...filteredDocuments]?.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a?.name?.toLowerCase();
        bValue = b?.name?.toLowerCase();
        break;
      case 'size':
        aValue = parseFloat(a?.size);
        bValue = parseFloat(b?.size);
        break;
      case 'type':
        aValue = a?.type;
        bValue = b?.type;
        break;
      default:
        aValue = new Date(a.lastModified);
        bValue = new Date(b.lastModified);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleFileUpload = (files) => {
    if (!files || files?.length === 0) {
      alert('No files selected');
      return;
    }
    
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'image/jpeg', 'image/png', 'image/gif'];
    
    const invalidFiles = [];
    const validFiles = [];
    
    Array.from(files)?.forEach(file => {
      if (file?.size > maxFileSize) {
        invalidFiles?.push(`${file?.name} (exceeds 50MB limit)`);
      } else if (!allowedTypes?.includes(file?.type)) {
        invalidFiles?.push(`${file?.name} (unsupported file type)`);
      } else {
        validFiles?.push(file);
      }
    });
    
    if (invalidFiles?.length > 0) {
      alert(`The following files cannot be uploaded:\n${invalidFiles?.join('\n')}`);
    }
    
    if (validFiles?.length > 0) {
      // Process valid files
      console.log('Uploading files:', validFiles);
      setShowUploadModal(false);
      // In production, this would upload to server/storage
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const files = Array.from(e?.dataTransfer?.files);
    handleFileUpload(files);
  };

  const handleDocumentSelect = (documentId) => {
    setSelectedDocuments(prev => 
      prev?.includes(documentId) 
        ? prev?.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocuments?.length === sortedDocuments?.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(sortedDocuments?.map(doc => doc?.id));
    }
  };

  const handleDocumentPreview = (document) => {
    setSelectedDocument(document);
    setShowPreview(true);
  };

  const handleShare = (document) => {
    setSelectedDocument(document);
    setShowShareModal(true);
  };

  const handleVersionHistory = (document) => {
    setSelectedDocument(document);
    setShowVersionHistory(true);
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return 'FileText';
      case 'docx': return 'FileText';
      case 'xlsx': return 'FileSpreadsheet';
      case 'zip': return 'Archive';
      case 'image': return 'Image';
      default: return 'File';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Final': return 'text-success bg-success-50';
      case 'Draft': return 'text-warning bg-warning-50';
      case 'Under Review': return 'text-primary bg-primary-50';
      case 'Executed': return 'text-success bg-success-50';
      default: return 'text-secondary bg-secondary-50';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="md:ml-64 transition-layout">
        {/* Header */}
        <div className="bg-surface border-b border-border px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold text-text-primary">Document Management</h1>
              <p className="text-text-secondary mt-1">Organize, share, and collaborate on legal documents</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center gap-2"
              >
                <Icon name="Upload" size={16} />
                Upload Documents
              </button>
              
              <button
                onClick={() => fileInputRef?.current?.click()}
                className="bg-secondary-100 text-text-primary px-4 py-2 rounded-lg font-medium hover:bg-secondary-200 transition-smooth focus:outline-none focus:ring-2 focus:ring-secondary-400 focus:ring-offset-2 flex items-center gap-2"
              >
                <Icon name="Plus" size={16} />
                Quick Upload
              </button>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(Array.from(e?.target?.files))}
              />
            </div>
          </div>
        </div>

        <div className="flex h-[calc(100vh-80px)]">
          {/* Left Sidebar - Folder Structure */}
          <div className="hidden lg:block w-80 bg-surface border-r border-border overflow-y-auto">
            <FolderStructure
              folders={mockFolders}
              selectedFolder={selectedFolder}
              onFolderSelect={setSelectedFolder}
            />
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search and Filter Bar */}
            <div className="bg-surface border-b border-border px-6 py-4">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 relative">
                  <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
                  <input
                    type="text"
                    placeholder="Search documents, cases, or tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e?.target?.value)}
                    className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {documentTypes?.map(type => (
                      <option key={type?.value} value={type?.value}>{type?.label}</option>
                    ))}
                  </select>
                  
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e?.target?.value)}
                    className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {sortOptions?.map(option => (
                      <option key={option?.value} value={option?.value}>{option?.label}</option>
                    ))}
                  </select>
                  
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="p-2 border border-border rounded-lg hover:bg-secondary-50 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <Icon name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={16} />
                  </button>
                  
                  <div className="flex border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('table')}
                      className={`p-2 transition-smooth ${viewMode === 'table' ? 'bg-primary text-white' : 'bg-surface hover:bg-secondary-50'}`}
                    >
                      <Icon name="List" size={16} />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 transition-smooth ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-surface hover:bg-secondary-50'}`}
                    >
                      <Icon name="Grid3X3" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedDocuments?.length > 0 && (
              <div className="bg-primary-50 border-b border-border px-6 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-primary">
                    {selectedDocuments?.length} document{selectedDocuments?.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button className="px-3 py-1 text-sm bg-surface border border-border rounded hover:bg-secondary-50 transition-smooth">
                      Download
                    </button>
                    <button className="px-3 py-1 text-sm bg-surface border border-border rounded hover:bg-secondary-50 transition-smooth">
                      Share
                    </button>
                    <button className="px-3 py-1 text-sm bg-surface border border-border rounded hover:bg-secondary-50 transition-smooth">
                      Move
                    </button>
                    <button className="px-3 py-1 text-sm text-error hover:bg-error-50 rounded transition-smooth">
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Document List/Grid */}
            <div 
              className={`flex-1 overflow-y-auto p-6 ${dragOver ? 'bg-primary-50 border-2 border-dashed border-primary' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {dragOver && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Icon name="Upload" size={48} className="mx-auto text-primary mb-4" />
                    <p className="text-lg font-medium text-primary">Drop files here to upload</p>
                    <p className="text-text-secondary">Supports PDF, Word, Excel, and image files</p>
                  </div>
                </div>
              )}

              {!dragOver && (
                <DocumentTable
                  documents={sortedDocuments}
                  selectedDocuments={selectedDocuments}
                  viewMode={viewMode}
                  onDocumentSelect={handleDocumentSelect}
                  onSelectAll={handleSelectAll}
                  onPreview={handleDocumentPreview}
                  onShare={handleShare}
                  onVersionHistory={handleVersionHistory}
                  getFileIcon={getFileIcon}
                  getStatusColor={getStatusColor}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleFileUpload}
        />
      )}
      {showShareModal && selectedDocument && (
        <ShareModal
          document={selectedDocument}
          onClose={() => setShowShareModal(false)}
        />
      )}
      {showVersionHistory && selectedDocument && (
        <VersionHistory
          document={selectedDocument}
          onClose={() => setShowVersionHistory(false)}
        />
      )}
      {showPreview && selectedDocument && (
        <DocumentPreview
          document={selectedDocument}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default DocumentManagement;